// SmartSearchProvider.ts - Fixed with playlist-specific caching, VPN and DNS support
import { fetchData } from "../workers/fetchWorkerManager";
import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { Buffer } from "buffer";
import { dnsResolver } from "./dnsResolver"; // Import your DNS resolver

class SmartSearchProvider {
  private static instance: SmartSearchProvider;
  private loadedCategories = new Map<string, any[]>(); // playlistKey_categoryId -> movies
  private allCategories = new Map<string, any[]>(); // playlistKey -> categories
  private currentlyLoading = new Set<string>();
  private currentPlaylistKey: string | null = null;

  static getInstance() {
    if (!this.instance) {
      this.instance = new SmartSearchProvider();
    }
    return this.instance;
  }

  // Generate unique playlist key
  private getPlaylistKey(): string {
    const username = playListUsername();
    const baseUrl = playListUrl();
    const password = playListPassword();
    return `${baseUrl}_${username}_${password}`;
  }

  // Helper function to build API URL with DNS and VPN support
  private async buildApiUrl(
    baseUrl: string,
    username: string,
    password: string,
    action: string,
    additionalParams: string = "",
  ): Promise<string> {
    let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;

    if (additionalParams) {
      apiUrl += `&${additionalParams}`;
    }

    // CUSTOM DNS TRANSFORMATION FIRST
    let finalApiUrl = apiUrl;
    try {
      console.log(`🌐 Starting DNS transformation for Smart Search (${action}): ${apiUrl}`);

      const dnsResult = await dnsResolver.transformUrl(apiUrl);

      console.log(`📡 DNS transformation result for Smart Search (${action}):`, dnsResult);

      if (typeof dnsResult === "object" && dnsResult.url) {
        finalApiUrl = dnsResult.url;
        console.log(`✅ Custom DNS applied to Smart Search (${action}): ${apiUrl} → ${finalApiUrl}`);
        // Note: Host header would be handled by fetchData if needed
      } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
        finalApiUrl = dnsResult;
        console.log(`🔄 Smart Search URL modified (${action}): ${finalApiUrl}`);
      } else {
        console.log(`📝 Smart Search URL unchanged (${action}) - localhost or DNS disabled`);
      }
    } catch (dnsError) {
      console.warn(`❌ DNS transformation failed for Smart Search (${action}):`, dnsError);
      finalApiUrl = apiUrl; // Use original URL
    }

    // Apply VPN proxy after DNS transformation
    const vpnStatus = localStorage.getItem("vpn");
    const proxyUrl = localStorage.getItem("proxy_url");

    console.log("@@@@@@@VPN", vpnStatus);

    if (vpnStatus === "on" && proxyUrl) {
      const bufferedUrl = Buffer.from(finalApiUrl, "utf-8").toString("base64");
      finalApiUrl = `${proxyUrl}${bufferedUrl}`;
      console.log(`🔒 VPN enabled for Smart Search (${action}) - routing through proxy: ${proxyUrl}`);
    }

    console.log(`📞 Final Smart Search API URL (${action}): ${finalApiUrl}`);
    return finalApiUrl;
  }

  // Check if playlist has changed and clear cache if needed
  private checkPlaylistChange(): void {
    const newPlaylistKey = this.getPlaylistKey();

    if (this.currentPlaylistKey && this.currentPlaylistKey !== newPlaylistKey) {
      console.log("🔄 Playlist changed, clearing search cache...");
      this.clearCache();
    }

    this.currentPlaylistKey = newPlaylistKey;
  }

  // Load the first 3 categories when search screen opens
  async initializeSearch(): Promise<{ totalMovies: number; categories: string[] }> {
    try {
      console.log("🚀 Initializing smart search with DNS support...");

      // Check if playlist changed
      this.checkPlaylistChange();

      const username = playListUsername();
      const password = playListPassword();
      const baseUrl = playListUrl();
      const playlistKey = this.getPlaylistKey();

      // Check if we already have categories for this playlist
      if (this.allCategories.has(playlistKey)) {
        console.log("📦 Using cached categories for this playlist");
      } else {
        // Get all categories first (this is fast - just category list) with DNS support
        const categoriesUrl = await this.buildApiUrl(baseUrl, username, password, "get_vod_categories");
        const categories = await fetchData(categoriesUrl);

        if (!Array.isArray(categories) || categories.length === 0) {
          throw new Error("No categories found");
        }

        // Cache categories for this playlist
        this.allCategories.set(playlistKey, categories);
        console.log(`📊 Found ${categories.length} categories for this playlist via DNS`);
      }

      const playlistCategories = this.allCategories.get(playlistKey)!;

      // Load first 3 categories (usually the most popular ones)
      const initialCategories = playlistCategories.slice(0, 3);
      let totalMovies = 0;
      const loadedCategoryNames = [];

      for (const category of initialCategories) {
        try {
          const movies = await this.loadCategoryMovies(category.category_id);
          totalMovies += movies.length;
          loadedCategoryNames.push(category.category_name);
          console.log(`✅ Loaded ${movies.length} movies from "${category.category_name}" via DNS`);
        } catch (error) {
          console.warn(`Failed to load category ${category.category_name} with DNS:`, error);
        }
      }

      console.log(
        `🎬 Initial load complete with DNS: ${totalMovies} movies from ${loadedCategoryNames.length} categories`,
      );

      return {
        totalMovies,
        categories: loadedCategoryNames,
      };
    } catch (error) {
      console.error("❌ Error initializing search with DNS:", error);
      throw error;
    }
  }

  // Load movies from a specific category with playlist-specific caching and DNS support
  private async loadCategoryMovies(categoryId: string): Promise<any[]> {
    const playlistKey = this.getPlaylistKey();
    const cacheKey = `${playlistKey}_${categoryId}`;

    // Return cached if already loaded for this playlist
    if (this.loadedCategories.has(cacheKey)) {
      return this.loadedCategories.get(cacheKey)!;
    }

    // Prevent duplicate loading
    if (this.currentlyLoading.has(cacheKey)) {
      console.log(`⏳ Category ${categoryId} already loading for this playlist, waiting...`);
      // Wait for it to finish (simple polling)
      while (this.currentlyLoading.has(cacheKey)) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.loadedCategories.get(cacheKey) || [];
    }

    this.currentlyLoading.add(cacheKey);

    try {
      const username = playListUsername();
      const password = playListPassword();
      const baseUrl = playListUrl();

      // Build URL with DNS support
      const url = await this.buildApiUrl(
        baseUrl,
        username,
        password,
        "get_vod_streams",
        `category_id=${categoryId}`,
      );
      const movies = await fetchData(url);

      if (Array.isArray(movies)) {
        this.loadedCategories.set(cacheKey, movies);
        console.log(
          `📦 Cached ${movies.length} movies for category ${categoryId} in playlist ${playlistKey} via DNS`,
        );
        return movies;
      }

      return [];
    } finally {
      this.currentlyLoading.delete(cacheKey);
    }
  }

  // Smart search that expands categories as needed with DNS support
  async smartSearch(
    query: string,
    maxResults: number = 50,
  ): Promise<{
    results: any[];
    searchedCategories: number;
    totalCategories: number;
  }> {
    // Check if playlist changed
    this.checkPlaylistChange();

    const playlistKey = this.getPlaylistKey();
    const playlistCategories = this.allCategories.get(playlistKey) || [];

    if (!query.trim()) {
      // Return some movies from loaded categories for empty search
      const allLoadedMovies = Array.from(this.loadedCategories.entries())
        .filter(([key]) => key.startsWith(playlistKey))
        .map(([, movies]) => movies)
        .flat();

      return {
        results: allLoadedMovies.slice(0, maxResults),
        searchedCategories: Array.from(this.loadedCategories.keys()).filter(key =>
          key.startsWith(playlistKey),
        ).length,
        totalCategories: playlistCategories.length,
      };
    }

    console.log(`🔍 Smart searching for: "${query}" in playlist ${playlistKey} with DNS support`);
    const queryLower = query.toLowerCase().trim();
    let results: any[] = [];
    let searchedCategories = 0;

    // Phase 1: Search in already loaded categories for this playlist
    console.log("📚 Phase 1: Searching loaded categories for this playlist...");
    for (const [cacheKey, movies] of this.loadedCategories) {
      if (!cacheKey.startsWith(playlistKey)) continue; // Skip other playlists

      const matches = movies.filter(movie => movie.name?.toLowerCase().includes(queryLower));
      results.push(...matches);
      searchedCategories++;

      if (results.length >= maxResults) {
        console.log(`✅ Found enough results in loaded categories: ${results.length}`);
        break;
      }
    }

    // Phase 2: If not enough results, load more categories with DNS support
    if (results.length < maxResults && searchedCategories < playlistCategories.length) {
      console.log(`📚 Phase 2: Need more results, loading additional categories with DNS...`);

      // Get loaded category IDs for this playlist
      const loadedCategoryIds = Array.from(this.loadedCategories.keys())
        .filter(key => key.startsWith(playlistKey))
        .map(key => key.replace(`${playlistKey}_`, ""));

      // Load next 3 categories that aren't already loaded
      const nextCategories = playlistCategories
        .filter(cat => !loadedCategoryIds.includes(cat.category_id))
        .slice(0, 3);

      for (const category of nextCategories) {
        try {
          console.log(`🔄 Loading category with DNS: ${category.category_name}`);
          const movies = await this.loadCategoryMovies(category.category_id);

          const matches = movies.filter(movie => movie.name?.toLowerCase().includes(queryLower));
          results.push(...matches);
          searchedCategories++;

          console.log(`📝 Found ${matches.length} matches in "${category.category_name}" via DNS`);

          if (results.length >= maxResults) {
            console.log(`✅ Found enough results: ${results.length}`);
            break;
          }
        } catch (error) {
          console.warn(`Failed to search category ${category.category_name} with DNS:`, error);
        }
      }
    }

    // Remove duplicates and limit results
    const uniqueResults = results
      .filter((movie, index, self) => index === self.findIndex(m => m.stream_id === movie.stream_id))
      .slice(0, maxResults);

    console.log(
      `🎯 Smart search complete with DNS: ${uniqueResults.length} results from ${searchedCategories}/${playlistCategories.length} categories`,
    );

    return {
      results: uniqueResults,
      searchedCategories,
      totalCategories: playlistCategories.length,
    };
  }

  // Get info about loaded categories for current playlist
  getLoadedInfo() {
    const playlistKey = this.getPlaylistKey();
    const playlistCategories = this.allCategories.get(playlistKey) || [];

    const totalMovies = Array.from(this.loadedCategories.entries())
      .filter(([key]) => key.startsWith(playlistKey))
      .reduce((sum, [, movies]) => sum + movies.length, 0);

    const loadedCategoryIds = Array.from(this.loadedCategories.keys())
      .filter(key => key.startsWith(playlistKey))
      .map(key => key.replace(`${playlistKey}_`, ""));

    return {
      loadedCategories: loadedCategoryIds.length,
      totalCategories: playlistCategories.length,
      totalMovies,
      categoryNames: playlistCategories
        .filter(cat => loadedCategoryIds.includes(cat.category_id))
        .map(cat => cat.category_name),
      dnsEnabled: dnsResolver.isCustomDnsEnabled(),
      dnsInfo: dnsResolver.getDnsInfo(),
    };
  }

  // Clear cache (can be for all playlists or specific playlist)
  clearCache(specificPlaylist?: string) {
    if (specificPlaylist) {
      // Clear cache for specific playlist
      const keysToDelete = Array.from(this.loadedCategories.keys()).filter(key =>
        key.startsWith(specificPlaylist),
      );
      keysToDelete.forEach(key => this.loadedCategories.delete(key));
      this.allCategories.delete(specificPlaylist);
      console.log(`🗑️ Search cache cleared for playlist: ${specificPlaylist}`);
    } else {
      // Clear all cache
      this.loadedCategories.clear();
      this.allCategories.clear();
      this.currentlyLoading.clear();
      this.currentPlaylistKey = null;
      console.log("🗑️ All search cache cleared");
    }
  }
}

export default SmartSearchProvider;
